import type {
    ForBlock,
    ImportDocumentSnapshot,
    PluginDefinition,
    PluginOutput,
} from "@magic-md/core";
import { WidgetType } from "@codemirror/view";
import { createElement, useCallback, useMemo } from "react";
import { createRoot, type Root } from "react-dom/client";
import { useShallow } from "zustand/react/shallow";
import { getNotificationBus } from "../../../store/notification-bus";
import { useEditorStore } from "../../../store/editor-store";
import { usePluginExecutionStore } from "../../../store/plugin-execution-store";
import { pluginUIRegistry } from "../../../plugins/plugin-ui-registry";
import { getPlatformService } from "../../../platform";
import { collectImportDocuments } from "../../../lib/import-documents";
import { collectParsedDocumentUseBindings } from "../../../lib/for-scope-units";
import { pickerInitialPathFromDocument } from "../../../lib/path-utils";
import {
    collectPinnedUnitIdsForDocument,
    createWorkspacePinnedUnit,
    findWorkspacePinnedUnit,
} from "../../../lib/pinned-units";
import { getDocumentBundleDir } from "../../../lib/publish-utils";
import { DocContextProvider } from "../../../components/document-renderer/contexts/DocContext";
import { ExecutionContextProvider } from "../../../components/document-renderer/contexts/ExecutionContext";
import { PluginBlock } from "../../../components/document-renderer/blocks/PluginBlock";
import { showFilePickerDialog, showFolderPickerDialog } from "../../../components/dialogs/DialogHost";

interface InlineRenderWidgetProps {
    definitionId: string;
    definitions: Map<string, PluginDefinition>;
    contentDefinitions: NonNullable<ReturnType<typeof useEditorStore.getState>["parsedDocument"]>["contentDefinitions"];
    documentPath: string | null;
}

export class InlineRenderWidget extends WidgetType {
    private root: Root | null = null;
    private readonly signature: string;

    constructor(
        readonly definitionId: string,
        readonly definitions: Map<string, PluginDefinition>,
        readonly contentDefinitions: InlineRenderWidgetProps["contentDefinitions"],
        readonly documentPath: string | null,
    ) {
        super();
        this.signature = buildInlineRenderWidgetSignature(
            definitionId,
            definitions,
            contentDefinitions,
            documentPath,
        );
    }

    eq(other: InlineRenderWidget): boolean {
        return other.signature === this.signature;
    }

    toDOM(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.className = "cm-magic-inline-render";
        this.root = createRoot(wrapper);
        this.root.render(
            createElement(InlineRenderPreview, {
                definitionId: this.definitionId,
                definitions: this.definitions,
                contentDefinitions: this.contentDefinitions,
                documentPath: this.documentPath,
            }),
        );
        return wrapper;
    }

    updateDOM(): boolean {
        return false;
    }

    destroy(): void {
        const root = this.root;
        this.root = null;
        if (!root) return;
        queueMicrotask(() => {
            root.unmount();
        });
    }

    ignoreEvent(): boolean {
        // Block widget: don't let CodeMirror handle events inside this widget.
        // The React component manages its own event handling.
        return true;
    }
}

function buildInlineRenderWidgetSignature(
    definitionId: string,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: InlineRenderWidgetProps["contentDefinitions"],
    documentPath: string | null,
): string {
    const definition = definitions.get(definitionId);
    if (definition) {
        return JSON.stringify({
            kind: "definition",
            definitionId,
            documentPath,
            pluginName: definition.pluginName,
            config: definition.config,
            outputMode: definition.outputMode,
            template: definition.template,
            params: definition.params,
        });
    }

    const contentDefinition = contentDefinitions?.get(definitionId);
    if (contentDefinition) {
        return JSON.stringify({
            kind: "content",
            definitionId,
            documentPath,
            body: contentDefinition.body,
            outputMode: contentDefinition.outputMode,
            template: contentDefinition.template,
            params: contentDefinition.params,
        });
    }

    return JSON.stringify({
        kind: "unknown",
        definitionId,
        documentPath,
    });
}

function InlineRenderPreview({
    definitionId,
    definitions,
    contentDefinitions,
    documentPath,
}: InlineRenderWidgetProps) {
    const {
        executions,
        forExecutions,
        contentExecutions,
        importExecutions,
        useExecutions,
        runAction,
        callApi,
        runPrimary,
        runImportAction,
        runForAction,
        cancelExecution,
        runContentAction,
        runUseAction,
        hydrateOutput,
        sessionId,
    } = usePluginExecutionStore(
        useShallow((s) => ({
            executions: s.executions,
            forExecutions: s.forExecutions,
            contentExecutions: s.contentExecutions,
            importExecutions: s.importExecutions,
            useExecutions: s.useExecutions,
            runAction: s.runAction,
            callApi: s.callApi,
            runPrimary: s.runPrimary,
            runImportAction: s.runImportAction,
            runForAction: s.runForAction,
            cancelExecution: s.cancelExecution,
            runContentAction: s.runContentAction,
            runUseAction: s.runUseAction,
            hydrateOutput: s.hydrateOutput,
            sessionId: s.sessionId,
        })),
    );

    const { pinnedUnits, rawTabs, parsedDocument, addPinnedUnit, removePinnedUnit } = useEditorStore(
        useShallow((s) => ({
            pinnedUnits: s.pinnedUnits,
            rawTabs: s.tabs,
            parsedDocument: s.parsedDocument,
            addPinnedUnit: s.addPinnedUnit,
            removePinnedUnit: s.removePinnedUnit,
        })),
    );

    const forBlocksMap = useMemo(() => {
        if (!parsedDocument) return undefined;
        const map = new Map<string, ForBlock>();
        for (const block of parsedDocument.blocks) {
            if (block.type === "for-block") {
                const forDefinition = parsedDocument.forDefinitions?.get(block.id);
                map.set(block.id, {
                    ...block,
                    template: forDefinition?.template ?? block.template,
                    params: forDefinition?.params ?? block.params,
                    externalRef: forDefinition?.externalRef ?? block.externalRef,
                });
            }
        }
        return map;
    }, [parsedDocument]);
    const useBindings = useMemo(
        () => collectParsedDocumentUseBindings(parsedDocument),
        [parsedDocument],
    );

    const importDocuments = useMemo<Map<string, ImportDocumentSnapshot>>(
        () => collectImportDocuments(parsedDocument?.imports, importExecutions),
        [parsedDocument?.imports, importExecutions],
    );

    // .map() must be outside the selector — useShallow compares references,
    // and .map() creates new arrays/objects on every call, causing infinite re-renders.
    const tabs = useMemo(
        () =>
            rawTabs.map((tab) => ({
                id: tab.id,
                title: tab.title,
                filePath: tab.filePath,
            })),
        [rawTabs],
    );

    const outputs = useMemo(() => {
        const map = new Map<string, PluginOutput>();
        for (const [id, def] of definitions) {
            const exec = executions.get(id);
            const partialOutput =
                exec?.status === "running" ? exec.progress?.partialOutput : undefined;
            const baseOutput = exec?.output ?? def.output ?? partialOutput;
            if (!baseOutput) continue;
            map.set(
                id,
                partialOutput && partialOutput !== baseOutput
                    ? { ...baseOutput, ...partialOutput }
                    : baseOutput,
            );
        }
        return map;
    }, [definitions, executions]);

    const executionState = useMemo(() => {
        const runningDefinitions = new Set<string>();
        const executionStatuses = new Map<string, "idle" | "running" | "success" | "error" | "cached">();
        const executionDurations = new Map<string, number>();
        const executionComputedAts = new Map<string, number>();
        const executionProgress = new Map<string, { message: string; percent?: number }>();
        const executionErrors = new Map<string, string>();

        for (const [id, exec] of executions) {
            executionStatuses.set(id, exec.status);
            if (exec.status === "running") {
                runningDefinitions.add(id);
                if (exec.progress) {
                    executionProgress.set(id, {
                        message: exec.progress.message,
                        percent: exec.progress.percent,
                    });
                }
            }
            if (exec.duration != null) executionDurations.set(id, exec.duration);
            if (exec.startedAt != null && exec.duration != null) {
                executionComputedAts.set(id, exec.startedAt + exec.duration);
            }
            if (exec.result && !exec.result.success && exec.result.message) {
                executionErrors.set(id, exec.result.message);
            }
        }

        return {
            runningDefinitions,
            executionStatuses,
            executionDurations,
            executionComputedAts,
            executionProgress,
            executionErrors,
        };
    }, [executions]);

    const onAction = useCallback(
        async (id: string, actionName: string, actionParams?: Record<string, unknown>) => {
            const definition = definitions.get(id);
            if (definition) {
                await runAction(definition, actionName, definitions, { actionParams });
            }
        },
        [definitions, runAction],
    );

    const onApi = useCallback(
        async <TData = unknown,>(id: string, apiName: string, params?: Record<string, unknown>) => {
            const definition = definitions.get(id);
            if (!definition) return { success: false, message: `Definition not found: ${id}` };
            return callApi<TData>(definition, apiName, definitions, params);
        },
        [callApi, definitions],
    );

    const onRun = useCallback(
        (id: string) => {
            const definition = definitions.get(id);
            if (definition) runPrimary(definition, definitions).catch(() => { });
        },
        [definitions, runPrimary],
    );

    const onPin = useCallback(
        (id: string) => {
            const definition = definitions.get(id);
            if (!documentPath || !definition) return;
            const existing = findWorkspacePinnedUnit(
                useEditorStore.getState().pinnedUnits,
                documentPath,
                id,
            );
            if (existing) {
                removePinnedUnit(existing.id);
                return;
            }
            void (async () => {
                const ensuredSessionId =
                    sessionId ?? await usePluginExecutionStore.getState()._ensureSession();
                addPinnedUnit(
                    createWorkspacePinnedUnit(documentPath, definition),
                    { sessionId: ensuredSessionId },
                );
            })();
        },
        [addPinnedUnit, definitions, documentPath, removePinnedUnit, sessionId],
    );

    const pickerInitialPath = pickerInitialPathFromDocument(documentPath);
    const documentBundlePath = documentPath ? getDocumentBundleDir(documentPath) : null;

    const docValue = useMemo(
        () => ({
            definitions,
            outputs,
            registry: pluginUIRegistry,
            executable: true,
            documentPath,
            documentBundlePath,
            editMode: true,
            contentDefinitions,
            forBlocksMap,
            useBindings,
            importDocuments,
            onAction,
            onApi,
            onRun,
            onPin,
            onDefinitionSelect: (id: string) => {
                const editor = useEditorStore.getState();
                editor.setRightSidebarDefinitionId(id);
                if (!editor.rightSidebarOpen) editor.toggleRightSidebar();
            },
            onDebug: (id: string) => {
                if (sessionId) {
                    import("../../../store/log-store").then(({ useLogStore }) => {
                        useLogStore.getState().openDebugPanel({
                            sessionId,
                            targetId: id,
                        });
                    });
                }
            },
            onRunFor: (id: string) => { runForAction(id).catch(() => { }); },
            onCancelFor: (id: string) => { cancelExecution(id); },
            onRunContent: (id: string) => { runContentAction(id).catch(() => { }); },
            onRunImport: (namespace: string) => { runImportAction(namespace).catch(() => { }); },
            onRunUse: (id: string) => { runUseAction(id).catch(() => { }); },
            onLoadOutput: (id: string) => hydrateOutput(id),
            callSidecar: (sidecarId: string, method: string, params?: Record<string, unknown>) =>
                getPlatformService().engine.sidecarCall(sidecarId, method, params),
            onEngineNotification: <T = unknown,>(method: string, handler: (params: T) => void) =>
                getNotificationBus().subscribe<T>(method, handler),
            selectFile: (options?: { accept?: string[]; title?: string }) =>
                showFilePickerDialog({
                    accept: options?.accept,
                    title: options?.title,
                    initialPath: pickerInitialPath,
                }),
            selectFolder: (options?: { title?: string }) =>
                showFolderPickerDialog({
                    title: options?.title,
                    initialPath: pickerInitialPath,
                }),
            openExternal: (url: string) => getPlatformService().openExternal?.(url) ?? Promise.resolve(),
            getDefinitions: () =>
                Array.from(definitions.values()).map((definition) => ({
                    id: definition.id,
                    pluginName: definition.pluginName,
                    hasOutput: outputs.has(definition.id),
                })),
            getTabs: () => tabs,
        }),
        [
            cancelExecution,
            contentDefinitions,
            definitions,
            forBlocksMap,
            useBindings,
            importDocuments,
            documentPath,
            documentBundlePath,
            onAction,
            onApi,
            onRun,
            onPin,
            outputs,
            pickerInitialPath,
            runContentAction,
            runForAction,
            runImportAction,
            runUseAction,
            hydrateOutput,
            sessionId,
            tabs,
        ],
    );

    const execValue = useMemo(
        () => ({
            pinnedDefinitions: collectPinnedUnitIdsForDocument(
                pinnedUnits,
                documentPath,
            ),
            ...executionState,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
            sessionConnected: !!sessionId,
        }),
        [
            contentExecutions,
            executionState,
            forExecutions,
            importExecutions,
            pinnedUnits,
            documentPath,
            sessionId,
            useExecutions,
        ],
    );

    return (
        <DocContextProvider value={docValue}>
            <ExecutionContextProvider value={execValue}>
                <div className="cm-magic-inline-render-content">
                    <PluginBlock definitionId={definitionId} attrs={{}} mode="block" />
                </div>
            </ExecutionContextProvider>
        </DocContextProvider>
    );
}
